<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $t('messages.absenteeism'),
                        href: meta.links.absenteeism_index,
                    },
                    {
                        name: $t('messages.compare'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.groups', 1)" for="group_id" />
                        <FormSelect
                            id="group_id"
                            v-model="form.group_id"
                            :options="options.groups"
                            class="mt-1 w-full"
                            null-option
                            :null-option-text="$t('messages.all')"
                        />
                        <FormError :value="form.errors.group_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2" v-if="options.school_years.length">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.school_year')" for="school_year_id" />
                        <FormSelect
                            id="school_year_id"
                            v-model="form.school_year_id"
                            :options="options.school_years"
                            class="mt-1 w-full"
                            :null-option="false"
                        />
                        <FormError :value="form.errors.school_year_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <FormLabel :value="$t('messages.absenteeism')" for="absenteeismPercent"> (>%)</FormLabel>
                    <FormInput
                        id="absenteeismPercent"
                        v-model="form.absenteeismPercent"
                        name="absenteeismPercent"
                        type="number"
                        min="1"
                        max="99"
                        step="1"
                        class="mt-1 w-full"
                    />
                    <FormError :value="form.errors.absenteeismPercent" class="mt-2" />
                </div>

                <div class="sm:col-span-2">
                    <FormLabel :value="$t('messages.date')" for="compare_date" />
                    <FormInput
                        id="compare_date"
                        v-model="form.compare_date"
                        name="compare_date"
                        type="date"
                        class="mt-1 w-full"
                    />
                    <FormError :value="form.errors.compare_date" class="mt-2" />
                </div>

                <div class="sm:col-span-2">
                    <FormLabel :value="$t('messages.show_only_changed')" for="changes" />
                    <input
                        type="checkbox"
                        name="changes"
                        id="changes"
                        v-model="form.changes"
                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                    <FormError :value="form.errors.changes" class="mt-2" />
                </div>

                <div class="sm:col-span-4">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-2 flex gap-x-2 items-center">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div v-if="attendanceRecords.length === 0" class="flex flex-col mt-5">
            <EmptyState picture />
        </div>
        <div class="flex flex-col mt-5" v-else>
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.groups', 2) }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.student') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ meta.info.compare_date }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ meta.info.year_range }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.trends', 1) }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(attendanceRecord, index) in attendanceRecords"
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                :key="attendanceRecord.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ attendanceRecord.student.group.name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <StudentInfoItem
                                        :student="attendanceRecord.student"
                                        v-if="attendanceRecord.student.active"
                                    />
                                    <template v-else>
                                        <span> {{ attendanceRecord.student.full_name }}</span>
                                        <BadgePill color="yellow" v-if="!attendanceRecord.student.active">
                                            {{ $t('messages.inactive') }}
                                        </BadgePill>
                                    </template>
                                </td>

                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ attendanceRecord.previousYearPercentageString }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ attendanceRecord.percentageString }}
                                </td>
                                <td>
                                    <div class="flex justify-start">
                                        <IconEqual v-if="attendanceRecord.trend === 'equal'" style="color: #a0aec0" />
                                        <IconTrendingUp
                                            class="mr-3"
                                            style="color: #fc8181"
                                            v-else-if="attendanceRecord.trend === 'up'"
                                        />
                                        <IconTrendingDown
                                            v-else-if="attendanceRecord.trend === 'down'"
                                            class="mr-3"
                                            style="color: #68d391"
                                        />
                                        <span class="ml-3">
                                            {{ attendanceRecord.diff }}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import EmptyState from '@/components/EmptyState.vue';
import BadgePill from '@/components/BadgePill.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import StudentInfoItem from '@/components/StudentInfoItem.vue';
import { IconEqual, IconTrendingDown, IconTrendingUp } from '@tabler/icons-vue';

export default {
    components: {
        IconEqual,
        IconTrendingDown,
        IconTrendingUp,
        StudentInfoItem,
        SubmitFormButton,
        FormInput,
        BadgePill,
        PanelPage,
        FormSelect,
        FormLabel,
        FormError,
        EmptyState,
    },
    layout: Layout,
    name: 'Index',
    props: ['attendanceRecords', 'options', 'filters', 'meta'],
    data() {
        return {
            form: this.$inertia.form({
                group_id: this.filters.group_id,
                school_year_id: this.filters.school_year_id,
                absenteeismPercent: this.filters.absenteeismPercent,
                compare_date: this.filters.compare_date,
                changes: this.filters.changes,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
